import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileDownloadUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Download, Image as ImageIcon, Music, FileAudio, Send, UploadCloud, Check, X } from "lucide-react";
import { toast } from "sonner";

const STAFF = ["super_admin", "operations", "accounts", "label_manager"];

export default function ReleaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rel, setRel] = useState(null);
  const [note, setNote] = useState("");
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionNote, setCorrectionNote] = useState("");
  const [replFile, setReplFile] = useState(null);
  const [repls, setRepls] = useState([]);
  const isStaff = STAFF.includes(user.role);
  const canReview = ["super_admin", "operations"].includes(user.role);
  const canSubmit = rel && rel.status === "DRAFT" && !isStaff;

  const loadRepls = () => {
    if (user.role === "super_admin") api.get("/file-replacements").then((r) => setRepls(r.data.filter((x) => x.release_id === id)));
  };
  const load = () => api.get(`/releases/${id}`).then((r) => setRel(r.data)).catch(() => nav("/releases"));
  useEffect(() => { load(); loadRepls(); /* eslint-disable-next-line */ }, [id]);

  if (!rel) return <p className="text-muted-foreground">Loading…</p>;

  const canReupload = ["super_admin", "operations", "label"].includes(user.role) && !["LIVE", "TAKEN DOWN"].includes(rel.status);

  const reupload = async () => {
    if (!replFile) return toast.error("Choose a WAV or FLAC file");
    const fd = new FormData();
    fd.append("file", replFile);
    try {
      const r = await api.post(`/releases/${id}/audio-reupload`, fd);
      toast.success(r.data.status === "applied" ? "Audio replaced" : "Replacement submitted for Super Admin approval");
      setReplFile(null); load(); loadRepls();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const decideRepl = async (rid, action) => {
    try {
      await api.post(`/file-replacements/${rid}/${action}`);
      toast.success(`Replacement ${action}d`);
      load(); loadRepls();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const artwork = rel.files?.find((f) => f.file_type === "artwork");
  const audios = rel.files?.filter((f) => f.file_type === "audio") || [];

  const setStatus = async (status, n = null) => {
    await api.post(`/releases/${id}/status`, { status, note: n });
    toast.success(`Status set to ${status}`);
    setCorrectionOpen(false);
    load();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/releases/${id}/notes`, { note });
    setNote("");
    toast.success("Internal note added");
    load();
  };
  const submitRelease = async () => {
    await api.post(`/releases/${id}/submit`);
    toast.success("Release submitted for review");
    load();
  };

  return (
    <div className="space-y-6" data-testid="release-detail-page">
      <Button variant="ghost" onClick={() => nav("/releases")} data-testid="back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-bold tracking-tight">{rel.title}</h1>
            <StatusBadge status={rel.status} />
          </div>
          <p className="mt-1 text-muted-foreground">{rel.artist_name} · {rel.release_type} · {rel.genre}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canSubmit && <Button onClick={submitRelease} data-testid="submit-release-btn"><Send className="mr-2 h-4 w-4" />Submit for Review</Button>}
          {canReview && (
            <>
              <Button variant="secondary" onClick={() => setStatus("UNDER REVIEW")} data-testid="review-btn">Under Review</Button>
              <Button variant="secondary" onClick={() => setCorrectionOpen(true)} data-testid="correction-btn">Request Correction</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus("APPROVED")} data-testid="approve-btn">Approve</Button>
              <Button variant="destructive" onClick={() => setStatus("REJECTED")} data-testid="reject-btn">Reject</Button>
              {rel.status === "APPROVED" && <Button className="bg-primary" onClick={() => setStatus("LIVE")} data-testid="live-btn">Set Live</Button>}
            </>
          )}
        </div>
      </div>

      {rel.correction_note && (
        <Card className="border-amber-500/30 bg-amber-500/5 p-4" data-testid="correction-note">
          <p className="text-sm font-semibold text-amber-400">Correction requested</p>
          <p className="text-sm text-muted-foreground">{rel.correction_note}</p>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <h3 className="mb-4 font-heading text-lg font-semibold">Metadata</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[
              ["Language", rel.language], ["Release Date", rel.release_date],
              ["Copyright Owner", rel.copyright_owner], ["Label", rel.label_name],
              ["UPC/EAN", rel.upc_ean || "—"], ["Submitted By", rel.submitted_by_name],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{k}</p>
                <p className="font-medium">{v || "—"}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-lg font-semibold">Artwork</h3>
          {artwork ? (
            <div className="space-y-3">
              <img src={fileDownloadUrl(artwork.id)} alt="artwork" className="aspect-square w-full rounded-lg border border-border object-cover" data-testid="artwork-preview" />
              <a href={fileDownloadUrl(artwork.id)} download={artwork.original_filename}>
                <Button variant="secondary" className="w-full" data-testid="download-artwork-btn"><Download className="mr-2 h-4 w-4" />Download Artwork</Button>
              </a>
            </div>
          ) : (
            <div className="flex aspect-square w-full flex-col items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground">
              <ImageIcon className="h-8 w-8" /><p className="mt-2 text-sm">No artwork</p>
            </div>
          )}
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-border p-4"><h3 className="font-heading text-lg font-semibold">Tracks</h3></div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead><TableHead>Title</TableHead><TableHead>Version</TableHead>
              <TableHead>Primary Artist</TableHead><TableHead>ISRC</TableHead><TableHead>Composer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rel.tracks?.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.track_number}</TableCell>
                <TableCell className="font-medium">{t.title}</TableCell>
                <TableCell>{t.version || "—"}</TableCell>
                <TableCell>{t.primary_artist || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{t.isrc || "—"}</TableCell>
                <TableCell>{t.composer || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-5">
        <h3 className="mb-4 font-heading text-lg font-semibold">Audio Files</h3>
        {audios.length === 0 ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Music className="h-5 w-5" />No audio uploaded.</div>
        ) : (
          <div className="space-y-2">
            {audios.map((f) => (
              <div key={f.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`audio-file-${f.id}`}>
                <div className="flex items-center gap-3">
                  <FileAudio className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{f.original_filename}</p>
                    <p className="text-xs text-muted-foreground">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <a href={fileDownloadUrl(f.id)} download={f.original_filename}>
                  <Button variant="secondary" size="sm" data-testid={`download-audio-${f.id}`}><Download className="mr-2 h-4 w-4" />Download WAV</Button>
                </a>
              </div>
            ))}
          </div>
        )}
        {canReupload && (
          <div className="mt-4 rounded-lg border border-dashed border-border p-4" data-testid="wav-reupload-section">
            <p className="text-sm font-semibold">Replace Audio (WAV/FLAC)</p>
            <p className="mb-3 text-xs text-muted-foreground">
              {user.role === "super_admin" ? "Replaces the current audio immediately." : "Your replacement needs Super Admin approval before it goes live."}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="file" accept=".wav,.flac" onChange={(e) => setReplFile(e.target.files[0])} data-testid="wav-reupload-input" className="max-w-xs" />
              <Button onClick={reupload} data-testid="wav-reupload-btn"><UploadCloud className="mr-2 h-4 w-4" />Submit Replacement</Button>
            </div>
          </div>
        )}
        {user.role === "super_admin" && repls.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="wav-replacement-approvals">
            <p className="text-sm font-semibold text-amber-400">Pending Audio Replacements</p>
            {repls.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`repl-row-${r.id}`}>
                <div>
                  <p className="text-sm font-medium">{r.new_file_name}</p>
                  <p className="text-xs text-muted-foreground">Requested by {r.requested_by_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decideRepl(r.id, "approve")} data-testid={`repl-approve-${r.id}`}><Check className="mr-1 h-4 w-4" />Approve</Button>
                  <Button size="sm" variant="destructive" onClick={() => decideRepl(r.id, "reject")} data-testid={`repl-reject-${r.id}`}><X className="mr-1 h-4 w-4" />Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {isStaff && (
        <Card className="p-5" data-testid="internal-notes-section">
          <h3 className="mb-3 font-heading text-lg font-semibold">Internal Notes <span className="text-xs font-normal text-muted-foreground">(staff only)</span></h3>
          <div className="space-y-2">
            {(rel.internal_notes || []).length === 0 && <p className="text-sm text-muted-foreground">No internal notes yet.</p>}
            {(rel.internal_notes || []).map((n, i) => (
              <div key={i} className="rounded-lg bg-accent p-3 text-sm">
                <p>{n.note}</p>
                <p className="mt-1 text-xs text-muted-foreground">{n.by} · {new Date(n.at).toLocaleString()}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="internal-note-input" />
            <Button onClick={addNote} data-testid="add-note-btn">Add</Button>
          </div>
        </Card>
      )}

      <Dialog open={correctionOpen} onOpenChange={setCorrectionOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Correction</DialogTitle></DialogHeader>
          <Textarea value={correctionNote} onChange={(e) => setCorrectionNote(e.target.value)} placeholder="Describe what needs correcting…" data-testid="correction-input" />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCorrectionOpen(false)}>Cancel</Button>
            <Button onClick={() => setStatus("CORRECTION REQUIRED", correctionNote)} data-testid="correction-submit">Send Correction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
